// WP6-B.5 — Marketing Projects tab. Ported verbatim from the live
// Studio G4 pod's `AdminProjects.js`. Only mechanical adjustment:
// `../../lib/api` → `./marketingAdminApi`.
import React, { useState, useEffect, useRef } from "react";
import {
    adminGetProjects,
    adminCreateProject,
    adminUpdateProject,
    adminDeleteProject,
    adminUploadProjectImage,
    adminUploadProjectCover,
    adminUpdateFeaturedProjects,
    getFeaturedProjects,
    getFileServeUrl,
} from "./marketingAdminApi";
import { Input } from "../../../components/ui/input";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../../../components/ui/dialog";
import {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
} from "../../../components/ui/table";
import {
    Plus,
    Pencil,
    Trash2,
    Upload,
    Image as ImageIcon,
    X,
    GripVertical,
    Star,
} from "lucide-react";
import RichTextEditor from "../../../components/RichTextEditor";

const emptyProject = {
    title: "",
    category: "",
    description: "",
    location: "",
    featured: false,
    images: [],
    cover_image: "",
};

export default function MarketingProjects() {
    const [projects, setProjects] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ ...emptyProject });
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadingCover, setUploadingCover] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [reorderDragIdx, setReorderDragIdx] = useState(null);
    const [reorderOverIdx, setReorderOverIdx] = useState(null);
    const fileInputRef = useRef(null);
    const coverInputRef = useRef(null);

    const [featuredOpen, setFeaturedOpen] = useState(false);
    const [featuredIds, setFeaturedIds] = useState([]);
    const [featuredOrientations, setFeaturedOrientations] = useState({});
    const [featuredSaving, setFeaturedSaving] = useState(false);
    const [featuredDragIdx, setFeaturedDragIdx] = useState(null);
    const [featuredOverIdx, setFeaturedOverIdx] = useState(null);

    const load = () => adminGetProjects().then(setProjects).catch(() => {});
    useEffect(() => {
        load();
    }, []);

    const openFeatured = async () => {
        try {
            const data = await getFeaturedProjects();
            setFeaturedIds(data.project_ids || []);
            setFeaturedOrientations(data.orientations || {});
        } catch {
            setFeaturedIds([]);
            setFeaturedOrientations({});
        }
        setFeaturedOpen(true);
    };

    const toggleFeatured = (projectId) => {
        setFeaturedIds((prev) =>
            prev.includes(projectId) ? prev.filter((id) => id !== projectId) : [...prev, projectId]
        );
    };

    const toggleOrientation = (projectId) => {
        setFeaturedOrientations((prev) => ({
            ...prev,
            [projectId]: prev[projectId] === "portrait" ? "landscape" : "portrait",
        }));
    };

    const handleFeaturedReorder = (dropIdx) => {
        if (featuredDragIdx === null || featuredDragIdx === dropIdx) {
            setFeaturedDragIdx(null);
            setFeaturedOverIdx(null);
            return;
        }
        setFeaturedIds((prev) => {
            const ids = [...prev];
            const [moved] = ids.splice(featuredDragIdx, 1);
            ids.splice(dropIdx, 0, moved);
            return ids;
        });
        setFeaturedDragIdx(null);
        setFeaturedOverIdx(null);
    };

    const saveFeatured = async () => {
        setFeaturedSaving(true);
        try {
            await adminUpdateFeaturedProjects(featuredIds, featuredOrientations);
            setFeaturedOpen(false);
        } catch (e) {
            console.error("Save featured error:", e);
            alert("Failed to save featured projects. Please try logging out and back in.");
        }
        setFeaturedSaving(false);
    };

    const openNew = () => {
        setEditing(null);
        setForm({ ...emptyProject });
        setDialogOpen(true);
    };

    const openEdit = (p) => {
        setEditing(p);
        setForm({
            title: p.title,
            category: p.category,
            description: p.description,
            location: p.location || "",
            featured: p.featured || false,
            images: p.images || [],
            cover_image: p.cover_image || "",
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            if (editing) {
                await adminUpdateProject(editing.id, form);
            } else {
                await adminCreateProject(form);
            }
            setDialogOpen(false);
            load();
        } catch (e) {
            console.error(e);
        }
        setSaving(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this project?")) return;
        await adminDeleteProject(id);
        load();
    };

    const handleFiles = async (files) => {
        if (!editing || !files.length) return;
        setUploading(true);
        try {
            for (const file of files) {
                if (!file.type.startsWith("image/")) continue;
                const result = await adminUploadProjectImage(editing.id, file);
                setForm((prev) => ({ ...prev, images: [...prev.images, result.id] }));
                setEditing((prev) => ({ ...prev, images: [...(prev.images || []), result.id] }));
            }
        } catch (e) {
            console.error("Upload error:", e);
        }
        setUploading(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragActive(false);
        handleFiles(Array.from(e.dataTransfer.files));
    };
    const handleDragOver = (e) => {
        e.preventDefault();
        setDragActive(true);
    };
    const handleDragLeave = () => setDragActive(false);

    const removeImage = (imageId) => {
        setForm((prev) => ({ ...prev, images: prev.images.filter((id) => id !== imageId) }));
    };

    const handleCoverUpload = async (file) => {
        if (!editing || !file) return;
        setUploadingCover(true);
        try {
            const result = await adminUploadProjectCover(editing.id, file);
            setForm((prev) => ({ ...prev, cover_image: result.id }));
            setEditing((prev) => ({ ...prev, cover_image: result.id }));
        } catch (e) {
            console.error("Cover upload error:", e);
        }
        setUploadingCover(false);
    };

    const removeCover = () => {
        setForm((prev) => ({ ...prev, cover_image: "" }));
    };

    const handleReorderDrop = (dropIdx) => {
        if (reorderDragIdx === null || reorderDragIdx === dropIdx) {
            setReorderDragIdx(null);
            setReorderOverIdx(null);
            return;
        }
        setForm((prev) => {
            const imgs = [...prev.images];
            const [moved] = imgs.splice(reorderDragIdx, 1);
            imgs.splice(dropIdx, 0, moved);
            return { ...prev, images: imgs };
        });
        setReorderDragIdx(null);
        setReorderOverIdx(null);
    };

    return (
        <div data-testid="admin-projects-page" className="p-8 md:p-12">
            <div className="flex items-center justify-between mb-10">
                <div>
                    <h1 className="font-['Outfit'] text-2xl tracking-tight text-[#0A0A0A]">Projects</h1>
                    <p className="text-sm text-[#525252] mt-1">Manage your portfolio</p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={openFeatured}
                        data-testid="featured-projects-btn"
                        className="btn-studio-outline px-6 py-2.5 text-sm tracking-wide font-medium inline-flex items-center gap-2"
                    >
                        <Star size={16} /> Featured Projects
                    </button>
                    <button
                        onClick={openNew}
                        data-testid="add-project-btn"
                        className="btn-studio px-6 py-2.5 text-sm tracking-wide font-medium inline-flex items-center gap-2"
                    >
                        <Plus size={16} /> Add Project
                    </button>
                </div>
            </div>

            <div className="border border-[#E5E5E5]">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-[#FAFAFA]">
                            <TableHead className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A]">Title</TableHead>
                            <TableHead className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A]">Category</TableHead>
                            <TableHead className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A]">Location</TableHead>
                            <TableHead className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A]">Images</TableHead>
                            <TableHead className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A]">Cover</TableHead>
                            <TableHead className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A] w-20">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {projects.map((p) => (
                            <TableRow key={p.id} data-testid={`project-row-${p.id}`}>
                                <TableCell className="text-sm text-[#0A0A0A] font-medium">{p.title}</TableCell>
                                <TableCell className="text-sm text-[#525252]">{p.category}</TableCell>
                                <TableCell className="text-sm text-[#525252]">{p.location}</TableCell>
                                <TableCell className="text-sm text-[#525252]">
                                    <span className="inline-flex items-center gap-1">
                                        <ImageIcon size={14} className="text-[#9A8B7E]" /> {(p.images || []).length}
                                    </span>
                                </TableCell>
                                <TableCell className="text-sm">
                                    {p.cover_image ? (
                                        <span className="text-green-600 text-xs font-bold tracking-wide uppercase">Yes</span>
                                    ) : (
                                        <span className="text-[#9A8B7E] text-xs tracking-wide uppercase">No</span>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => openEdit(p)}
                                            data-testid={`edit-project-${p.id}`}
                                            className="text-[#525252] hover:text-[#0A0A0A] transition-colors"
                                        >
                                            <Pencil size={16} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(p.id)}
                                            data-testid={`delete-project-${p.id}`}
                                            className="text-[#525252] hover:text-red-600 transition-colors"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                        {projects.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} className="text-center text-sm text-[#525252] py-8">
                                    No projects yet
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="rounded-none max-w-2xl max-h-[85vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-['Outfit'] text-xl tracking-tight">
                            {editing ? "Edit Project" : "New Project"}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                        <div>
                            <label className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A] mb-1.5 block">Title</label>
                            <Input
                                data-testid="project-title-input"
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                className="rounded-none border-[#E5E5E5] h-10"
                            />
                        </div>
                        <div>
                            <label className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A] mb-1.5 block">Category</label>
                            <Input
                                data-testid="project-category-input"
                                value={form.category}
                                onChange={(e) => setForm({ ...form, category: e.target.value })}
                                className="rounded-none border-[#E5E5E5] h-10"
                                placeholder="Residential, Commercial..."
                            />
                        </div>
                        <div>
                            <label className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A] mb-1.5 block">Location</label>
                            <Input
                                data-testid="project-location-input"
                                value={form.location}
                                onChange={(e) => setForm({ ...form, location: e.target.value })}
                                className="rounded-none border-[#E5E5E5] h-10"
                            />
                        </div>
                        <div>
                            <label className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A] mb-1.5 block">Description</label>
                            <RichTextEditor
                                content={form.description}
                                onChange={(html) => setForm((prev) => ({ ...prev, description: html }))}
                                placeholder="Describe the project..."
                            />
                        </div>
                        {editing && (
                            <div>
                                <label className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A] mb-3 block">
                                    Cover Image
                                    <span className="font-normal normal-case tracking-normal text-[#9A8B7E] ml-2">
                                        — shown on project cards
                                    </span>
                                </label>
                                {form.cover_image ? (
                                    <div className="relative group inline-block">
                                        <img
                                            src={getFileServeUrl(form.cover_image)}
                                            alt="Cover"
                                            data-testid="project-cover-preview"
                                            className="w-48 aspect-[16/10] object-cover border border-[#E5E5E5]"
                                        />
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2">
                                            <button
                                                onClick={() => coverInputRef.current?.click()}
                                                data-testid="change-cover-btn"
                                                className="bg-white text-[#0A0A0A] px-3 py-1.5 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                Change
                                            </button>
                                            <button
                                                onClick={removeCover}
                                                data-testid="remove-cover-btn"
                                                className="bg-black/60 text-white px-3 py-1.5 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => coverInputRef.current?.click()}
                                        data-testid="upload-cover-btn"
                                        disabled={uploadingCover}
                                        className="border-2 border-dashed border-[#E5E5E5] hover:border-[#9A8B7E] px-6 py-4 text-sm text-[#525252] transition-colors flex items-center gap-2"
                                    >
                                        <Upload size={16} className="text-[#9A8B7E]" />
                                        {uploadingCover ? "Uploading..." : "Upload Cover Image"}
                                    </button>
                                )}
                                <input
                                    ref={coverInputRef}
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    data-testid="cover-file-input"
                                    onChange={(e) => {
                                        const f = e.target.files?.[0];
                                        if (f) handleCoverUpload(f);
                                        e.target.value = "";
                                    }}
                                    disabled={uploadingCover}
                                />
                            </div>
                        )}
                        {editing && (
                            <div>
                                <label className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A] mb-3 block">Project Images</label>
                                {form.images.length > 0 && (
                                    <>
                                        <div className="grid grid-cols-3 gap-2 mb-3">
                                            {form.images.map((imageId, i) => (
                                                <div
                                                    key={imageId}
                                                    data-testid={`project-image-${i}`}
                                                    draggable
                                                    onDragStart={() => setReorderDragIdx(i)}
                                                    onDragOver={(e) => {
                                                        e.preventDefault();
                                                        setReorderOverIdx(i);
                                                    }}
                                                    onDragLeave={() => setReorderOverIdx(null)}
                                                    onDrop={(e) => {
                                                        e.preventDefault();
                                                        handleReorderDrop(i);
                                                    }}
                                                    onDragEnd={() => {
                                                        setReorderDragIdx(null);
                                                        setReorderOverIdx(null);
                                                    }}
                                                    className={`relative group aspect-[4/3] bg-[#FAFAFA] border overflow-hidden cursor-grab active:cursor-grabbing transition-all duration-150
                                                        ${reorderOverIdx === i && reorderDragIdx !== i ? "border-[#650505] ring-2 ring-[#650505]/20 scale-[1.02]" : "border-[#E5E5E5]"}
                                                        ${reorderDragIdx === i ? "opacity-40" : "opacity-100"}`}
                                                >
                                                    <img
                                                        src={getFileServeUrl(imageId)}
                                                        alt={`Project ${i + 1}`}
                                                        className="w-full h-full object-cover pointer-events-none"
                                                    />
                                                    <div className="absolute top-1.5 left-1.5 bg-black/50 text-white p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <GripVertical size={14} />
                                                    </div>
                                                    <button
                                                        onClick={() => removeImage(imageId)}
                                                        data-testid={`remove-image-${i}`}
                                                        className="absolute top-1.5 right-1.5 bg-black/60 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                                    >
                                                        <X size={12} />
                                                    </button>
                                                    <span className="absolute bottom-1.5 left-1.5 text-[10px] bg-black/50 text-white px-1.5 py-0.5 tabular-nums">
                                                        {i + 1}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                        <p className="text-xs text-[#9A8B7E] mb-3">
                                            Drag to reorder. First image is used as fallback cover.
                                        </p>
                                    </>
                                )}
                                <div
                                    data-testid="image-dropzone"
                                    className={`border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
                                        dragActive ? "border-[#0A0A0A] bg-[#FAFAFA]" : "border-[#E5E5E5] hover:border-[#9A8B7E]"
                                    }`}
                                    onDrop={handleDrop}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <Upload size={20} className="mx-auto mb-2 text-[#9A8B7E]" />
                                    <p className="text-sm text-[#525252]">
                                        {uploading ? "Uploading..." : "Drop images here or click to browse"}
                                    </p>
                                    <p className="text-xs text-[#9A8B7E] mt-1">JPG, PNG, WebP — max 10MB each</p>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        className="hidden"
                                        data-testid="image-file-input"
                                        onChange={(e) => handleFiles(Array.from(e.target.files))}
                                        disabled={uploading}
                                    />
                                </div>
                            </div>
                        )}
                        {!editing && (
                            <p className="text-xs text-[#9A8B7E] italic">
                                Save the project first, then edit it to upload images.
                            </p>
                        )}
                        <div className="flex gap-3 pt-4">
                            <button
                                onClick={handleSave}
                                data-testid="save-project-btn"
                                disabled={saving}
                                className="btn-studio px-6 py-2.5 text-sm font-medium disabled:opacity-50"
                            >
                                {saving ? "Saving..." : editing ? "Update" : "Create"}
                            </button>
                            <button
                                onClick={() => setDialogOpen(false)}
                                className="btn-studio-outline px-6 py-2.5 text-sm font-medium"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog open={featuredOpen} onOpenChange={setFeaturedOpen}>
                <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="featured-projects-modal">
                    <DialogHeader>
                        <DialogTitle className="font-['Outfit'] text-xl tracking-tight">Featured Projects</DialogTitle>
                        <p className="text-sm text-[#525252] mt-1">
                            Select and reorder projects shown in the homepage "Selected Projects" section.
                        </p>
                    </DialogHeader>
                    <div className="space-y-1 mt-4">
                        {featuredIds.length > 0 && (
                            <div className="mb-4">
                                <p className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A] mb-2">
                                    Selected ({featuredIds.length})
                                </p>
                                <div className="space-y-1">
                                    {featuredIds.map((id, i) => {
                                        const p = projects.find((pr) => pr.id === id);
                                        if (!p) return null;
                                        return (
                                            <div
                                                key={id}
                                                draggable
                                                onDragStart={() => setFeaturedDragIdx(i)}
                                                onDragOver={(e) => {
                                                    e.preventDefault();
                                                    setFeaturedOverIdx(i);
                                                }}
                                                onDragLeave={() => setFeaturedOverIdx(null)}
                                                onDrop={(e) => {
                                                    e.preventDefault();
                                                    handleFeaturedReorder(i);
                                                }}
                                                onDragEnd={() => {
                                                    setFeaturedDragIdx(null);
                                                    setFeaturedOverIdx(null);
                                                }}
                                                data-testid={`featured-item-${i}`}
                                                className={`flex items-center gap-3 px-3 py-2.5 border cursor-grab active:cursor-grabbing transition-all duration-150
                                                    ${featuredOverIdx === i && featuredDragIdx !== i ? "border-[#650505] bg-[#650505]/5" : "border-[#E5E5E5] bg-white"}
                                                    ${featuredDragIdx === i ? "opacity-40" : ""}`}
                                            >
                                                <GripVertical size={14} className="text-[#9A8B7E] shrink-0" />
                                                <span className="text-xs font-bold text-[#9A8B7E] w-5 shrink-0">{i + 1}</span>
                                                {p.cover_image || (p.images && p.images.length > 0) ? (
                                                    <img
                                                        src={getFileServeUrl(p.cover_image || p.images[0])}
                                                        alt=""
                                                        className="w-10 h-10 object-cover shrink-0"
                                                    />
                                                ) : (
                                                    <div className="w-10 h-10 bg-[#F0F0F0] flex items-center justify-center shrink-0">
                                                        <ImageIcon size={14} className="text-[#9A8B7E]" />
                                                    </div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-[#0A0A0A] truncate">{p.title}</p>
                                                    <p className="text-xs text-[#9A8B7E]">{p.category}</p>
                                                </div>
                                                <div className="flex items-center gap-1.5 shrink-0">
                                                    <button
                                                        onClick={() =>
                                                            setFeaturedOrientations((prev) => ({ ...prev, [id]: "landscape" }))
                                                        }
                                                        data-testid={`featured-landscape-${i}`}
                                                        title="Landscape"
                                                        className={`p-1 border transition-colors ${
                                                            featuredOrientations[id] !== "portrait"
                                                                ? "border-[#0A0A0A] bg-[#0A0A0A]"
                                                                : "border-[#E5E5E5] hover:border-[#0A0A0A]"
                                                        }`}
                                                    >
                                                        <div
                                                            className={`w-5 h-3.5 ${featuredOrientations[id] !== "portrait" ? "bg-white" : "bg-[#E5E5E5]"}`}
                                                        />
                                                    </button>
                                                    <button
                                                        onClick={() =>
                                                            setFeaturedOrientations((prev) => ({ ...prev, [id]: "portrait" }))
                                                        }
                                                        data-testid={`featured-portrait-${i}`}
                                                        title="Portrait"
                                                        className={`p-1 border transition-colors ${
                                                            featuredOrientations[id] === "portrait"
                                                                ? "border-[#0A0A0A] bg-[#0A0A0A]"
                                                                : "border-[#E5E5E5] hover:border-[#0A0A0A]"
                                                        }`}
                                                    >
                                                        <div
                                                            className={`w-3.5 h-5 ${featuredOrientations[id] === "portrait" ? "bg-white" : "bg-[#E5E5E5]"}`}
                                                        />
                                                    </button>
                                                </div>
                                                <button
                                                    onClick={() => toggleFeatured(id)}
                                                    data-testid={`featured-remove-${i}`}
                                                    className="text-[#525252] hover:text-[#0A0A0A] shrink-0 ml-1"
                                                >
                                                    <X size={14} />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {projects.filter((p) => !featuredIds.includes(p.id)).length > 0 && (
                            <div>
                                <p className="text-xs tracking-[0.1em] uppercase font-bold text-[#525252] mb-2">Available Projects</p>
                                <div className="space-y-1">
                                    {projects
                                        .filter((p) => !featuredIds.includes(p.id))
                                        .map((p) => (
                                            <button
                                                key={p.id}
                                                onClick={() => toggleFeatured(p.id)}
                                                data-testid={`featured-add-${p.id}`}
                                                className="flex items-center gap-3 px-3 py-2.5 border border-dashed border-[#E5E5E5] hover:border-[#0A0A0A] w-full text-left transition-colors"
                                            >
                                                <Plus size={14} className="text-[#9A8B7E] shrink-0" />
                                                {p.cover_image || (p.images && p.images.length > 0) ? (
                                                    <img
                                                        src={getFileServeUrl(p.cover_image || p.images[0])}
                                                        alt=""
                                                        className="w-10 h-10 object-cover shrink-0"
                                                    />
                                                ) : (
                                                    <div className="w-10 h-10 bg-[#F0F0F0] flex items-center justify-center shrink-0">
                                                        <ImageIcon size={14} className="text-[#9A8B7E]" />
                                                    </div>
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-[#0A0A0A] truncate">{p.title}</p>
                                                    <p className="text-xs text-[#9A8B7E]">{p.category}</p>
                                                </div>
                                            </button>
                                        ))}
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="flex gap-3 pt-4 border-t border-[#E5E5E5] mt-4">
                        <button
                            onClick={saveFeatured}
                            data-testid="save-featured-btn"
                            disabled={featuredSaving}
                            className="btn-studio px-6 py-2.5 text-sm font-medium disabled:opacity-50"
                        >
                            {featuredSaving ? "Saving..." : "Save"}
                        </button>
                        <button
                            onClick={() => setFeaturedOpen(false)}
                            className="btn-studio-outline px-6 py-2.5 text-sm font-medium"
                        >
                            Cancel
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
